/**
 * Who this account is, and the one thing about it that can be edited.
 *
 * The email address is shown but not editable. It is the sign-in identity and the unique
 * key on the account, so changing it without proving the new address is how an account
 * quietly becomes somebody else's — the server refuses it for the same reason.
 */
import { useEffect, useState } from 'react'

import { Field, FormNote } from '@/components/profile/Field'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Controls'
import { Panel } from '@/components/ui/Panel'
import { useUpdateDisplayName } from '@/features/profile/useProfile'
import { toApiError } from '@/lib/api/errors'
import { formatTimestamp } from '@/lib/utils/format'

import type { components } from '@/types/generated/api'

type Profile = components['schemas']['ProfileResponse']

export function IdentityPanel({ profile }: { profile: Profile }) {
  const update = useUpdateDisplayName()
  const [name, setName] = useState(profile.display_name ?? '')
  const [saved, setSaved] = useState(false)

  // The server is the source of truth: a save elsewhere, or a reload, must not be
  // overwritten by a stale draft sitting in this component.
  useEffect(() => {
    setName(profile.display_name ?? '')
  }, [profile.display_name])

  const dirty = name.trim() !== (profile.display_name ?? '').trim()
  const error = update.error ? toApiError(update.error) : null

  return (
    <Panel title="Your account">
      <div className="flex items-start gap-4 border-b border-line/60 px-4 py-4">
        {/* A Google or GitHub photo when there is one; initials when it is missing, slow or
            blocked — never a broken-image icon on someone's own page. */}
        <Avatar
          name={profile.display_name || profile.email || 'user'}
          src={profile.avatar_url}
          size="lg"
          className="h-14 w-14 text-lg"
        />
        <div className="min-w-0">
          <p className="truncate text-base text-ink">{profile.display_name ?? 'Unnamed account'}</p>
          <p className="mono truncate text-xs text-ink-muted">{profile.email ?? 'No email address'}</p>
          <p className="mt-1.5 text-xs text-ink-faint">
            Joined {formatTimestamp(profile.created_at)}
            {profile.last_login_at ? ` · last signed in ${formatTimestamp(profile.last_login_at)}` : ''}
          </p>
        </div>
      </div>

      <form
        className="space-y-3 px-4 py-4"
        onSubmit={(event) => {
          event.preventDefault()
          setSaved(false)
          update.mutate(name.trim(), { onSuccess: () => setSaved(true) })
        }}
      >
        <Field
          label="Display name"
          value={name}
          onChange={(value) => {
            setSaved(false)
            setName(value)
          }}
          autoComplete="name"
          hint="Shown in the account menu and on this page. It is not used to sign in."
        />
        <div className="flex items-center gap-3">
          <Button type="submit" variant="primary" disabled={!dirty || update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
          {saved ? <FormNote tone="ok">Saved.</FormNote> : null}
        </div>
        {error ? <FormNote tone="error">{error.message}</FormNote> : null}
      </form>
    </Panel>
  )
}
